import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'

import { sortByKey, sortByDateKey, sortByFileSize } from '../../util/sorting'
import { handleDownloadResponse } from '../../util/downloadFile'
import { Container, Stackable, FirstItem, LargeItem, MedItem, SmallItem, SmallItemRight } from '../styles/CategoryStyles'
import { Divider, FlexColumn, FlexRow } from '../styles/Containers'
import { TableCheckbox, TableCheckboxCell } from '../styles/Checkbox'
import { TableHeaderButton } from '../common/Button'
import BodyText from '../styles/Text'
import CategoryOriginalFile from './CategoryOriginalFile'
import { SelectionBar } from './FileBulkActionsBar'
import InlineFilePreview from './InlineFilePreview'
import useInlineFilePreview from '../../hooks/useInlineFilePreview'
import useFileSelection from '../../hooks/useFileSelection'

export const categoryRemovedFilesTableStrings = {
  restoreToCategory: 'Restore to category',
  selectAll: 'Select all files'
}

const TableContainer = styled(FlexColumn)`
  min-width: 350px;
  flex-grow: 1;
`

const Empty = styled(BodyText)`
  display: flex;
  justify-content: center;
  padding-top: 15px;
`

const Headings = styled(FlexRow)`
  padding: 0 20px 10px;
  align-items: center;
`

const HeaderButton = styled(TableHeaderButton)`
  font-size: 16px;
  font-weight: 400;
  white-space: nowrap;

  @container (max-width: 450px) {
    justify-content: flex-start;
  }
`

const UnfoldMoreIconRight = styled(UnfoldMoreIcon)`
  margin-right: -22px;
`

const ButtonColumn = styled.div`
  min-width: 30px;
`

function CategoryRemovedFilesTable({
  files = [],
  foiId,
  categories = [],
  preview = null,
  onPreviewConsumed,
  selectable = false,
  disabled = false,
  handleRestoreFile = () => {},
  onRestoreSelected
}) {
  const [sortBy, setSortBy] = useState('filename')
  const [sortedFiles, setSortedFiles] = useState()
  const [sortAscending, setSortAscending] = useState(true)

  const filePreview = useInlineFilePreview({ files, preview, onPreviewConsumed })

  function sort(newSortBy) {
    if (newSortBy === sortBy) {
      setSortedFiles(prev => [...prev].reverse())
      setSortAscending(!sortAscending)
    } else {
      setSortBy(newSortBy)
      setSortAscending(true)

      const copy = [...sortedFiles]
      if (['createdAt', 'archivedAt'].includes(newSortBy)) setSortedFiles(copy.sort(sortByDateKey(newSortBy)))
      else if (newSortBy === 'size') setSortedFiles(copy.sort(sortByFileSize))
      else setSortedFiles(copy.sort(sortByKey(newSortBy)))
    }
  }

  useEffect(() => {
    const sortedFileList = [...files]
    if (sortBy) {
      if (['createdAt', 'archivedAt'].includes(sortBy)) {
        sortedFileList.sort(sortByDateKey(sortBy))
      } else if (sortBy === 'size') {
        sortedFileList.sort(sortByFileSize)
      } else {
        sortedFileList.sort(sortByKey(sortBy))
      }
      if (!sortAscending) {
        sortedFileList.reverse()
      }
    }
    setSortedFiles(sortedFileList)
  }, [files])

  function handleRowClick(downloadResult, file) {
    if (file.fileExtension === '.pdf') {
      filePreview.showFromResponse(downloadResult, file)
    } else {
      handleDownloadResponse(downloadResult, file.filename, 'download')
      filePreview.close()
    }
  }

  const isRemoved = true
  const canSelect = selectable && !disabled

  const selection = useFileSelection({ files, resetKey: foiId })

  const shownIds = (sortedFiles ?? []).map(f => f.id)
  const selectedFiles = (sortedFiles ?? []).filter(f => selection.isSelected(f.id))
  const allShownSelected = selection.allSelected(shownIds)

  const bulkActions = onRestoreSelected
    ? [
        {
          label: categoryRemovedFilesTableStrings.restoreToCategory,
          icon: <SettingsBackupRestoreIcon />,
          onClick: () => onRestoreSelected(selectedFiles)
        }
      ]
    : []

  return (
    <FlexRow>
      <TableContainer gap="0">
        {canSelect && <SelectionBar count={selection.selectedIds.size} actions={bulkActions} />}
        <Headings gap="30px">
          {canSelect && (
            <TableCheckboxCell>
              <TableCheckbox
                checked={allShownSelected}
                indeterminate={selection.someSelected(shownIds) && !allShownSelected}
                onChange={() => selection.toggleAll(shownIds)}
                inputProps={{ 'aria-label': categoryRemovedFilesTableStrings.selectAll }}
              />
            </TableCheckboxCell>
          )}
          <FirstItem>Original File Type</FirstItem>
          <Container>
            <Stackable width="100%">
              <LargeItem isRemoved={isRemoved}>
                <HeaderButton variant="text" onClick={() => sort('filename')} active={(sortBy === 'filename').toString()}>
                  Filename <UnfoldMoreIcon />
                </HeaderButton>
              </LargeItem>
              <MedItem>
                <HeaderButton variant="text" onClick={() => sort('archivedReason')} active={(sortBy === 'archivedReason').toString()}>
                  Reason <UnfoldMoreIcon />
                </HeaderButton>
              </MedItem>

              <SmallItem isRemoved={isRemoved}>
                <HeaderButton variant="text" onClick={() => sort('archivedAt')} active={(sortBy === 'archivedAt').toString()}>
                  Removed
                  <UnfoldMoreIcon />
                </HeaderButton>
              </SmallItem>
              <SmallItemRight isRemoved={isRemoved}>
                <HeaderButton variant="text" onClick={() => sort('size')} active={(sortBy === 'size').toString()}>
                  Size <UnfoldMoreIconRight />
                </HeaderButton>
              </SmallItemRight>
            </Stackable>
          </Container>
          <ButtonColumn data-testid="actions-header" />
        </Headings>
        <Divider />
        {sortedFiles && sortedFiles.length > 0 ? (
          sortedFiles.map(f => {
            return (
              <div key={f.id} data-testid="table-row">
                <CategoryOriginalFile
                  file={f}
                  foiId={foiId}
                  categories={categories}
                  isRemoved={isRemoved}
                  selectable={canSelect}
                  selected={canSelect && selection.isSelected(f.id)}
                  onToggleSelect={() => selection.toggle(f.id)}
                  active={f.id === filePreview.activeFile?.id}
                  onRowClick={handleRowClick}
                  handleRestoreFile={handleRestoreFile}
                  disabled={disabled}
                />
                <Divider />
              </div>
            )
          })
        ) : (
          <Empty>No files available.</Empty>
        )}
      </TableContainer>
      <InlineFilePreview preview={filePreview} />
    </FlexRow>
  )
}

CategoryRemovedFilesTable.propTypes = {
  files: PropTypes.array,
  foiId: PropTypes.string.isRequired,
  categories: PropTypes.array,
  preview: PropTypes.shape({
    fileId: PropTypes.string.isRequired,
    filename: PropTypes.string,
    page: PropTypes.number,
    nonce: PropTypes.number
  }),
  onPreviewConsumed: PropTypes.func,
  isRemoved: PropTypes.bool,
  selectable: PropTypes.bool,
  disabled: PropTypes.bool,
  handleRestoreFile: PropTypes.func,
  onRestoreSelected: PropTypes.func
}

export default CategoryRemovedFilesTable
